import React, { ReactNode } from 'react';

import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { Separator } from '@/blocks/components/Separator';
import { LinkI18n } from '@/i18n/routing';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import {
  UseConverterProps,
  useConverter,
} from '@/app/[lang]/buy-crypto/components/ConverterWidget/useConverter';

import { ConverterRow } from './ConverterRow';
import styles from './ConverterWidget.module.scss';

function XRate({
  selectedToken,
  isLoading,
  currencyRate,
  selectedFiat,
  error,
}: {
  selectedToken: string;
  currencyRate: number;
  selectedFiat: string;
  isLoading: boolean;
  error: boolean;
}) {
  return (
    <div className={typography.bodyM}>
      <p className={styles.rates}>
        <span>1 {selectedToken} = </span>{' '}
        <span className={styles.currencyRate}>
          {isLoading ? (
            <Skeleton animation={'wave'} />
          ) : (
            <>
              {currencyRate} {selectedFiat}
            </>
          )}
        </span>
      </p>
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}

export function ConverterHeader({ selectedToken }: { selectedToken: string }) {
  const tr = useTranslations('pages.buyCrypto.converterWidget');

  return (
    <div className={styles.title}>
      <h2 className={typography.h4}>{tr.rich('title', { coinTicker: selectedToken })}</h2>
    </div>
  );
}

export function ConverterFooter() {
  const tr = useTranslations('pages.buyCrypto.converterWidget');

  return (
    <p className={classNames(typography.bodyS, styles.footnote)}>
      {tr.rich('footnote', {
        a: (content) => (
          <LinkI18n href="#" className={buttons.btnLink}>
            {content}
          </LinkI18n>
        ),
      })}
    </p>
  );
}

interface ConverterWidgetProps extends UseConverterProps {
  renderCta(data: {
    selectedFiat: string;
    selectedToken: string;
    payAmount: string;
    getAmount: string;
  }): ReactNode;
}

export const ConverterWidget = ({
  // default values should be set on useConverter
  shouldNavigate,
  initialSelectedToken,
  //
  renderCta,
}: ConverterWidgetProps) => {
  const {
    isLoading,
    error,
    payAmount,
    getAmount,
    fiatOptions,
    tokenOptions,
    selectedFiat,
    selectedToken,
    currencyRate,
    handlePayAmountChange,
    handleGetAmountChange,
    handleTokenSelectChange,
    handleFiatSelectChange,
  } = useConverter({ shouldNavigate, initialSelectedToken });

  const tr = useTranslations('pages.buyCrypto.converterWidget');

  return (
    <>
      <div className={styles.body}>
        <ConverterRow
          isFiat
          label={tr.rich('labelPay')}
          amount={payAmount || ''}
          options={fiatOptions}
          selectedOption={selectedFiat}
          onSelectChange={handleFiatSelectChange}
          onAmountChange={handlePayAmountChange}
          disabled={isLoading || fiatOptions.length === 0}
        />
        <Separator className={styles.separator} />
        <ConverterRow
          label={tr.rich('labelGet')}
          amount={getAmount || ''}
          options={tokenOptions}
          selectedOption={selectedToken}
          onSelectChange={handleTokenSelectChange}
          onAmountChange={handleGetAmountChange}
          disabled={isLoading || tokenOptions.length === 0}
        />
        <XRate
          selectedToken={selectedToken}
          isLoading={isLoading}
          currencyRate={currencyRate}
          selectedFiat={selectedFiat}
          error={error}
        />
        {renderCta({ selectedToken, selectedFiat, payAmount, getAmount })}
      </div>
    </>
  );
};
